"use client";

import { CitySelect } from "@/components/city-select";
import { CountrySelect } from "@/components/country-select";
import { DatePicker } from "@/components/date-picker";
import { Gender, type GuestDataProps } from "@/components/GuestForm";
import { ProvinceSelect } from "@/components/province-select";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { environment } from "@/config/env";
import { useEliteStructure } from "@/contexts/EliteTemporaryContextProvider";
import { getAllowedDocumentTypes, useDocumentOcr } from "@/hooks/use-ocr";
import type { OcrResponse } from "@/types/OcrResponse";
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  IdCard,
  Info,
  Loader2,
  Lock,
  Mars,
  Receipt,
  ScanLine,
  ShieldCheck,
  User,
  Venus,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PMSUserVerificationDialog } from "@/components/PMSUserVerificationDialog";
import {
  PrivacyPolicyDialog,
  useStructurePrivacy,
} from "@/components/PrivacyPolicyDialog";
import { DocumentCardUpload } from "./DocumentCardUpload";

interface GuestDialogFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  guest: GuestDataProps;
  roomDescription?: string;
  guestIndex: number;
  onSave: (guest: GuestDataProps) => void;
}

export function GuestDialogForm({
  isOpen,
  onOpenChange,
  guest,
  roomDescription,
  guestIndex,
  onSave,
}: GuestDialogFormProps) {
  const { t: tGuest } = useTranslation("guestForm");
  const { privacyPolicyHtml, marketingPolicyHtml } = useStructurePrivacy();

  const [formData, setFormData] = useState<GuestDataProps>({
    ...guest,
    privacyPolicy: guest.privacyPolicy ?? false,
  });
  const [activeTab, setActiveTab] = useState<string>("document");
  const [isOcrLoading, setIsOcrLoading] = useState<boolean>(false);
  const [ocrSuccess, setOcrSuccess] = useState<boolean>(false);
  const [ocrDone, setOcrDone] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isPrivacyInfoDialogOpen, setIsPrivacyInfoDialogOpen] =
    useState<boolean>(false);
  const [policyModalType, setPolicyModalType] = useState<
    "privacy" | "marketing"
  >("privacy");
  const [isConfirmCancelOpen, setIsConfirmCancelOpen] =
    useState<boolean>(false);
  const [isPmsVerificationOpen, setIsPmsVerificationOpen] =
    useState<boolean>(false);
  const [hasVerifiedPms, setHasVerifiedPms] = useState<boolean>(false);
  const { processDocument, updateDocumentData } = useDocumentOcr();

  const allowedDocumentTypes = getAllowedDocumentTypes(
    formData.issuingCountry?.iso2 ||
      formData.birthCountry?.iso2 ||
      formData.nationality,
  );

  useEffect(() => {
    if (isOpen) {
      setFormData({
        ...guest,
        privacyPolicy: guest.privacyPolicy ?? false,
      });
      setErrors({});
      setOcrSuccess(false);
      setOcrDone(false);
      setHasVerifiedPms(Boolean(guest.pmsId || guest.crmId));
      setIsPmsVerificationOpen(false);
      setActiveTab("document");
    }
  }, [isOpen, guest]);

  // Se il paese cambia e il tipo documento selezionato non è permesso per quella nazione, svuota il campo
  useEffect(() => {
    if (
      formData.documentType &&
      !allowedDocumentTypes.includes(formData.documentType)
    ) {
      setFormData((prev) => ({ ...prev, documentType: "" }));
    }
  }, [
    formData.issuingCountry?.iso2,
    formData.birthCountry?.iso2,
    formData.nationality,
  ]);

  // Apri il dialog di verifica anagrafica PMS/CRM quando si accede al tab privacy
  useEffect(() => {
    if (
      activeTab === "privacy" &&
      !hasVerifiedPms &&
      (formData.name?.trim() ||
        formData.surname?.trim() ||
        formData.email?.trim() ||
        formData.taxCode?.trim())
    ) {
      setIsPmsVerificationOpen(true);
      setHasVerifiedPms(true);
    }
  }, [activeTab]);

  const handleChange = (field: keyof GuestDataProps, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (
      field === "name" ||
      field === "surname" ||
      field === "email" ||
      field === "birthDate" ||
      field === "taxCode"
    ) {
      setHasVerifiedPms(false);
    }
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const getBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFrontFileChange = async (fileWithPreview: any) => {
    if (!fileWithPreview?.file) {
      handleChange("frontDocumentData", undefined);
      handleChange("base64FrontData", undefined);
      setOcrDone(false);
      setOcrSuccess(false);
      return;
    }

    try {
      const file = fileWithPreview.file;
      const base64 = await getBase64(file);
      handleChange("frontDocumentData", file);
      handleChange("base64FrontData", base64);
      setOcrDone(false);
      setOcrSuccess(false);
    } catch (err) {
      console.error("Error reading front document", err);
    }
  };

  const handleBackFileChange = async (fileWithPreview: any) => {
    if (!fileWithPreview?.file) {
      handleChange("backDocumentData", undefined);
      handleChange("base64BackData", undefined);
      setOcrDone(false);
      setOcrSuccess(false);
      return;
    }

    try {
      const file = fileWithPreview.file;
      const base64 = await getBase64(file);
      handleChange("backDocumentData", file);
      handleChange("base64BackData", base64);
      setOcrDone(false);
      setOcrSuccess(false);
    } catch (err) {
      console.error("Error reading back document", err);
    }
  };

  const handleContinueToAnagraphic = async () => {
    const hasDocumentToScan = !!formData.frontDocumentData;

    if (hasDocumentToScan && !ocrDone) {
      setIsOcrLoading(true);
      processDocument(
        formData.frontDocumentData,
        (response: OcrResponse) => {
          if (response?.data) {
            const updated = updateDocumentData(
              formData,
              response,
              formData.backDocumentData || ({} as File),
              formData.base64BackData || "",
            );
            setFormData((prev) => ({
              ...prev,
              ...updated,
              frontDocumentData: formData.frontDocumentData,
              backDocumentData: formData.backDocumentData,
              base64FrontData: formData.base64FrontData,
              base64BackData: formData.base64BackData,
            }));
            setOcrDone(true);
            setOcrSuccess(Boolean(updated.documentType || updated.name));
            if (updated.isDocumentExpired) {
              setErrors((prev) => ({
                ...prev,
                expirationDate: tGuest(
                  "validation.docExpired",
                  "Il documento risulta scaduto",
                ),
              }));
              toast.warning(
                tGuest(
                  "documentTab.docExpiredWarning",
                  "Attenzione: il documento scansionato risulta scaduto. La data di scadenza non è stata inserita.",
                ),
                {
                  richColors: true,
                  duration: 6000,
                  description: tGuest(
                    "documentTab.docExpiredWarningDesc",
                    "La data di scadenza del documento è passata. Inserisci un documento in corso di validità.",
                  ),
                },
              );
            } else if (updated.documentType || updated.name) {
              toast.success(
                tGuest(
                  "documentTab.scanSuccessTitle",
                  "Documento riconosciuto con successo!",
                ),
                { richColors: true },
              );
            } else {
              toast.info(
                tGuest(
                  "documentTab.continueManual",
                  "Non è stato possibile estrarre tutti i dati automaticamente. Puoi compilarli manualmente.",
                ),
                { richColors: true },
              );
            }
          }
          setIsOcrLoading(false);
          setActiveTab("anagraphic");
        },
        (error) => {
          console.warn("OCR extraction error", error);
          setIsOcrLoading(false);
          toast.info(
            tGuest(
              "documentTab.continueManual",
              "Non è stato possibile estrarre tutti i dati automaticamente. Puoi compilarli manualmente.",
            ),
            { richColors: true },
          );
          setActiveTab("anagraphic");
        },
        formData.backDocumentData,
      );
    } else {
      setActiveTab("anagraphic");
    }
  };

  const applyForeignCityFallbacks = (data: GuestDataProps): GuestDataProps => {
    const next = { ...data };
    if (
      next.birthCountry &&
      next.birthCountry.iso2?.toUpperCase() !== "IT" &&
      !next.birthCity?.name?.trim() &&
      next.birthCountry.name
    ) {
      next.birthCity = { id: "0", name: next.birthCountry.name };
    }
    if (
      next.residenceCountry &&
      next.residenceCountry.iso2?.toUpperCase() !== "IT" &&
      !next.residenceCity?.name?.trim() &&
      next.residenceCountry.name
    ) {
      next.residenceCity = { id: "0", name: next.residenceCountry.name };
    }
    if (
      next.issuingCountry &&
      next.issuingCountry.iso2?.toUpperCase() !== "IT" &&
      !next.issuingCity?.name?.trim() &&
      next.issuingCountry.name
    ) {
      next.issuingCity = { id: "0", name: next.issuingCountry.name };
    }
    return next;
  };

  const validate = (dataToValidate: GuestDataProps = formData): boolean => {
    const newErrors: Record<string, string> = {};

    if (!dataToValidate.name?.trim())
      newErrors.name = tGuest("validation.nameRequired");
    if (!dataToValidate.surname?.trim())
      newErrors.surname = tGuest("validation.surnameRequired");
    if (!dataToValidate.birthDate)
      newErrors.birthDate = tGuest("validation.birthDateRequired");
    if (dataToValidate.gender === undefined || dataToValidate.gender === null) {
      newErrors.gender = tGuest("validation.genderRequired");
    }

    if (!dataToValidate.birthCountry?.iso2) {
      newErrors.birthCountry = tGuest("validation.birthCountryRequired");
    }

    if (dataToValidate.birthCountry?.iso2?.toUpperCase() === "IT") {
      if (!dataToValidate.birthProvince?.code) {
        newErrors.birthProvince = tGuest("validation.birthProvinceRequired");
      }
      if (!dataToValidate.birthCity?.name?.trim()) {
        newErrors.birthCity = tGuest("validation.birthCityRequired");
      }
      // Codice fiscale non è obbligatorio
    }

    if (!dataToValidate.residenceCountry?.iso2) {
      newErrors.residenceCountry = tGuest(
        "validation.residenceCountryRequired",
      );
    }
    if (!dataToValidate.residenceAddress?.trim()) {
      newErrors.residenceAddress = tGuest(
        "validation.residenceAddressRequired",
      );
    }

    if (dataToValidate.residenceCountry?.iso2?.toUpperCase() === "IT") {
      if (!dataToValidate.residenceProvince?.code) {
        newErrors.residenceProvince = tGuest(
          "validation.residenceProvinceRequired",
        );
      }
      if (!dataToValidate.residenceCity?.name?.trim()) {
        newErrors.residenceCity = tGuest(
          "validation.residenceCityRequired",
        );
      }
      if (!dataToValidate.cap?.trim()) {
        newErrors.cap = tGuest("validation.capRequired");
      }
    }

    if (!dataToValidate.email?.trim()) {
      newErrors.email = tGuest("validation.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dataToValidate.email)) {
      newErrors.email = tGuest("validation.emailInvalid");
    }

    // Controllo estremi documento (Task 1219509298518740):
    // Tipo documento sempre obbligatorio e coerente con la nazione
    const currentAllowedTypes = getAllowedDocumentTypes(
      dataToValidate.issuingCountry?.iso2 ||
        dataToValidate.birthCountry?.iso2 ||
        dataToValidate.nationality,
    );
    if (
      !dataToValidate.documentType?.trim() ||
      !currentAllowedTypes.includes(dataToValidate.documentType)
    ) {
      newErrors.documentType = tGuest(
        "validation.docTypeRequired",
        "Seleziona il tipo di documento",
      );
    }
    if (!dataToValidate.documentNumber?.trim()) {
      newErrors.documentNumber = tGuest(
        "validation.docNumberRequired",
        "Numero di documento obbligatorio",
      );
    }
    if (!dataToValidate.issuingCountry?.iso2) {
      newErrors.issuingCountry = tGuest(
        "validation.issuingCountryRequired",
        "Paese di rilascio obbligatorio",
      );
    }
    if (dataToValidate.issuingCountry?.iso2?.toUpperCase() === "IT") {
      if (!dataToValidate.issuingCity?.name?.trim()) {
        newErrors.issuingCity = tGuest(
          "validation.issuingCityRequired",
          "Comune di rilascio obbligatorio",
        );
      }
    }
    if (!dataToValidate.issuingAuthority?.trim()) {
      newErrors.issuingAuthority = tGuest(
        "validation.issuingAuthorityRequired",
        "Ente di rilascio obbligatorio",
      );
    }

    // Controllo data di emissione documento
    if (!dataToValidate.issuingDate) {
      newErrors.issuingDate = tGuest(
        "validation.issuingDateRequired",
        "Data di emissione obbligatoria",
      );
    } else {
      const issDate = new Date(dataToValidate.issuingDate);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      if (issDate > todayEnd) {
        newErrors.issuingDate = tGuest(
          "validation.issuingDateInvalid",
          "La data di emissione non può essere futura",
        );
      }
    }

    // Controllo scadenza documento
    if (!dataToValidate.expirationDate) {
      newErrors.expirationDate = tGuest(
        "validation.expirationDateRequired",
        "Data di scadenza obbligatoria",
      );
    } else {
      const expDate = new Date(dataToValidate.expirationDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (expDate < today) {
        newErrors.expirationDate = tGuest("validation.docExpired");
      }
    }

    if (!dataToValidate.privacyPolicy) {
      newErrors.privacyPolicy = tGuest("validation.privacyRequired");
    }

    if (dataToValidate.invoiceRequest) {
      if (!dataToValidate.companyName?.trim())
        newErrors.companyName = tGuest("validation.companyNameRequired");
      if (!dataToValidate.vatNumber?.trim())
        newErrors.vatNumber = tGuest("validation.vatNumberRequired");
      if (!dataToValidate.invoiceAddress?.trim())
        newErrors.invoiceAddress = tGuest(
          "validation.invoiceAddressRequired",
        );
      if (!dataToValidate.invoiceCity?.trim())
        newErrors.invoiceCity = tGuest("validation.invoiceCityRequired");
      if (!dataToValidate.invoiceCap?.trim())
        newErrors.invoiceCap = tGuest("validation.invoiceCapRequired");
      if (
        !dataToValidate.recipientCode?.trim() &&
        !dataToValidate.pecEmail?.trim()
      ) {
        newErrors.recipientCode = tGuest(
          "validation.recipientCodeRequired",
        );
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      if (
        newErrors.name ||
        newErrors.surname ||
        newErrors.birthDate ||
        newErrors.gender ||
        newErrors.birthCountry ||
        newErrors.birthProvince ||
        newErrors.birthCity ||
        newErrors.residenceCountry ||
        newErrors.residenceProvince ||
        newErrors.residenceCity ||
        newErrors.residenceAddress ||
        newErrors.cap ||
        newErrors.email ||
        newErrors.issuingCountry ||
        newErrors.issuingCity ||
        newErrors.issuingDate ||
        newErrors.expirationDate ||
        newErrors.issuingAuthority ||
        newErrors.documentType ||
        newErrors.documentNumber
      ) {
        setActiveTab("anagraphic");
      } else if (
        newErrors.privacyPolicy ||
        newErrors.companyName ||
        newErrors.vatNumber ||
        newErrors.invoiceAddress ||
        newErrors.invoiceCity ||
        newErrors.invoiceCap ||
        newErrors.recipientCode
      ) {
        setActiveTab("privacy");
      }
      toast.error(tGuest("validation.fillRequired"), {
        richColors: true,
      });
      return false;
    }

    return true;
  };

  const handleSave = () => {
    const normalizedData = applyForeignCityFallbacks(formData);
    setFormData(normalizedData);
    if (!validate(normalizedData)) return;
    onSave(normalizedData);
    onOpenChange(false);
    toast.success(
      `${normalizedData.name} ${normalizedData.surname} - ${tGuest("buttons.save")}`,
      { richColors: true },
    );
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            if (!isOcrLoading) {
              setIsConfirmCancelOpen(true);
            }
          } else {
            onOpenChange(true);
          }
        }}
      >
        <DialogContent className="sm:max-w-3xl max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden">
          {/* Loading Overlay durante l'estrazione OCR */}
          {isOcrLoading && (
            <div className="absolute inset-0 z-50 bg-background/85 backdrop-blur-xs flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-200">
              <div className="relative mb-5">
                <div className="size-20 rounded-full bg-primary/10 flex items-center justify-center text-primary shadow-sm border border-primary/20">
                  <ScanLine className="size-10 text-primary animate-pulse" />
                </div>
                <div className="absolute -bottom-1 -right-1 bg-background rounded-full p-1 shadow-xs border">
                  <Loader2 className="size-6 animate-spin text-primary" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-foreground">
                {tGuest("documentTab.extracting")}
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm mt-2 leading-relaxed">
                {tGuest("documentTab.scanDesc")}
              </p>
              <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground/80 bg-muted/50 px-3 py-1.5 rounded-full">
                <ShieldCheck className="size-3.5 text-emerald-600" />
                SSL & Privacy Protection
              </div>
            </div>
          )}

          {/* Header */}
          <DialogHeader className="p-4 sm:p-6 pb-3 border-b bg-muted/20">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  <User className="size-5 text-primary" />
                  {tGuest("guestTitle", { index: guestIndex + 1 })}
                </DialogTitle>
                <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
                  {roomDescription
                    ? tGuest("assignedTo", { room: roomDescription })
                    : tGuest("anagraphicTab.personalData")}
                </DialogDescription>
              </div>
              {formData.name && (
                <Badge
                  variant="outline"
                  className="hidden sm:inline-flex bg-background"
                >
                  {formData.name} {formData.surname}
                </Badge>
              )}
            </div>

            {/* Navigation Tabs */}
            <Tabs
              value={activeTab}
              onValueChange={(tab) => !isOcrLoading && setActiveTab(tab)}
              className="w-full mt-3"
            >
              <TabsList className="grid grid-cols-3 w-full h-10">
                <TabsTrigger
                  value="document"
                  className="text-xs sm:text-sm gap-1.5"
                  disabled={isOcrLoading}
                >
                  <IdCard className="size-4" />
                  {tGuest("tabs.document")}
                </TabsTrigger>
                <TabsTrigger
                  value="anagraphic"
                  className="text-xs sm:text-sm gap-1.5"
                  disabled={isOcrLoading}
                >
                  <User className="size-4" />
                  {tGuest("tabs.anagraphic")}
                </TabsTrigger>
                <TabsTrigger
                  value="privacy"
                  className="text-xs sm:text-sm gap-1.5"
                  disabled={isOcrLoading}
                >
                  <ShieldCheck className="size-4" />
                  {tGuest("tabs.privacy")}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </DialogHeader>

          {/* Scrollable Body */}
          <ScrollArea className="flex-1 p-4 sm:p-6 overflow-y-auto">
            {/* TAB 1: DOCUMENTO & OCR */}
            {activeTab === "document" && (
              <div className="space-y-5 animate-in fade-in duration-200">
                {/* Bug 1: Warning Extra-UE per passaporto */}
                <Alert className="bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-200">
                  <AlertIcon>
                    <AlertCircle className="size-4 text-amber-600" />
                  </AlertIcon>
                  <AlertTitle className="text-xs sm:text-sm font-semibold">
                    {tGuest("documentVerification.foreignNationalityWarning")}
                  </AlertTitle>
                </Alert>

                <Alert className="bg-primary/5 border-primary/20">
                  <AlertIcon>
                    <ScanLine className="size-4 text-primary" />
                  </AlertIcon>
                  <AlertTitle className="text-sm font-semibold">
                    {tGuest("documentTab.scanTitle")}
                  </AlertTitle>
                  <AlertDescription className="text-xs text-muted-foreground">
                    {tGuest("documentTab.scanDesc")}
                  </AlertDescription>
                </Alert>

                {ocrSuccess && (
                  <Alert className="bg-emerald-500/10 border-emerald-500/20 text-emerald-800 dark:text-emerald-300">
                    <AlertIcon>
                      <CheckCircle2 className="size-4 text-emerald-600" />
                    </AlertIcon>
                    <AlertTitle className="text-sm font-semibold">
                      {tGuest("documentTab.scanSuccessTitle")}
                    </AlertTitle>
                    <AlertDescription className="text-xs">
                      {tGuest("documentTab.scanSuccessDesc")}
                    </AlertDescription>
                  </Alert>
                )}

                {formData.frontDocumentData && !ocrDone && (
                  <Alert className="bg-primary/5 border-primary/20 text-foreground">
                    <AlertIcon>
                      <CheckCircle2 className="size-4 text-primary" />
                    </AlertIcon>
                    <AlertTitle className="text-sm font-semibold">
                      {tGuest("documentTab.readyToExtractTitle")}
                    </AlertTitle>
                    <AlertDescription className="text-xs text-muted-foreground">
                      {formData.backDocumentData
                        ? tGuest("documentTab.readyToExtractBothDesc")
                        : tGuest("documentTab.readyToExtractDesc")}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DocumentCardUpload
                    label={tGuest("documentTab.frontLabel")}
                    sublabel={tGuest("documentTab.frontSublabel")}
                    value={formData.frontDocumentData}
                    onFileChange={handleFrontFileChange}
                    disabled={isOcrLoading}
                  />
                  <DocumentCardUpload
                    label={tGuest("documentTab.backLabel")}
                    sublabel={tGuest("documentTab.backSublabel")}
                    value={formData.backDocumentData}
                    onFileChange={handleBackFileChange}
                    disabled={isOcrLoading}
                  />
                </div>

                <div className="pt-3 border-t flex flex-col sm:flex-row items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground text-center sm:text-left">
                    {!formData.frontDocumentData
                      ? tGuest("documentTab.continueManual")
                      : formData.backDocumentData
                        ? tGuest("documentTab.readyToExtractBothDesc")
                        : tGuest("documentTab.readyToExtractDesc")}
                  </p>
                  <Button
                    type="button"
                    variant="default"
                    disabled={isOcrLoading}
                    onClick={handleContinueToAnagraphic}
                    className="w-full sm:w-auto min-w-[200px]"
                  >
                    {isOcrLoading ? (
                      <>
                        <Loader2 className="size-4 animate-spin mr-2" />
                        {tGuest("documentTab.extracting")}
                      </>
                    ) : formData.frontDocumentData && !ocrDone ? (
                      <>{tGuest("documentTab.continueWithOcr")}</>
                    ) : (
                      <>{tGuest("documentTab.continueWithoutOcr")}</>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* TAB 2: DATI ANAGRAFICI */}
            {activeTab === "anagraphic" && (
              <div className="space-y-6 animate-in fade-in duration-200">
                {/* Dati Personali Base */}
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                    <User className="size-4 text-primary" />
                    {tGuest("anagraphicTab.personalData")}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.name")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        placeholder={tGuest("placeholders.name")}
                        value={formData.name || ""}
                        onChange={(e) => handleChange("name", e.target.value)}
                        className={errors.name ? "border-destructive" : ""}
                      />
                      {errors.name && (
                        <p className="text-xs text-destructive">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.surname")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        placeholder={tGuest("placeholders.surname")}
                        value={formData.surname || ""}
                        onChange={(e) =>
                          handleChange("surname", e.target.value)
                        }
                        className={errors.surname ? "border-destructive" : ""}
                      />
                      {errors.surname && (
                        <p className="text-xs text-destructive">
                          {errors.surname}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.birthDate")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <DatePicker
                        value={formData.birthDate ?? undefined}
                        onChange={(date) => handleChange("birthDate", date)}
                        placeholder="GG/MM/AAAA"
                        className={`w-full ${errors.birthDate ? "border-destructive" : ""}`}
                      />
                      {errors.birthDate && (
                        <p className="text-xs text-destructive">
                          {errors.birthDate}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.gender")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={
                          formData.gender !== undefined &&
                          formData.gender !== null
                            ? formData.gender.toString()
                            : ""
                        }
                        onValueChange={(val) =>
                          handleChange("gender", parseInt(val))
                        }
                      >
                        <SelectTrigger
                          className={`w-full ${errors.gender ? "border-destructive" : ""}`}
                        >
                          <SelectValue
                            placeholder={tGuest("anagraphicTab.selectGender")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">
                            <span className="flex items-center gap-1.5">
                              <Mars className="size-4 text-blue-500" />{" "}
                              {tGuest("anagraphicTab.male")}
                            </span>
                          </SelectItem>
                          <SelectItem value="1">
                            <span className="flex items-center gap-1.5">
                              <Venus className="size-4 text-pink-500" />{" "}
                              {tGuest("anagraphicTab.female")}
                            </span>
                          </SelectItem>
                          <SelectItem value="2">
                            {tGuest("anagraphicTab.other")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.gender && (
                        <p className="text-xs text-destructive">
                          {errors.gender}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Nascita & Cittadinanza */}
                <div className="pt-2 border-t">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                    <FileText className="size-4 text-primary" />
                    {tGuest("anagraphicTab.birthAndTaxCode")}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.birthCountry")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <CountrySelect
                        value={formData.birthCountry}
                        apiEndpoint={`${environment.ELITE_BASE_URI}/geo/countries`}
                        onValueChange={(country) =>
                          handleChange("birthCountry", country)
                        }
                        placeholder={tGuest("anagraphicTab.selectCountry")}
                      />
                      {errors.birthCountry && (
                        <p className="text-xs text-destructive">
                          {errors.birthCountry}
                        </p>
                      )}
                    </div>

                    {formData.birthCountry?.iso2?.toUpperCase() === "IT" ? (
                      <>
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("anagraphicTab.birthProvince")}{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <ProvinceSelect
                            value={formData.birthProvince}
                            onValueChange={(prov) =>
                              handleChange("birthProvince", prov)
                            }
                            placeholder={tGuest(
                              "anagraphicTab.selectProvince",
                            )}
                          />
                          {errors.birthProvince && (
                            <p className="text-xs text-destructive">
                              {errors.birthProvince}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("anagraphicTab.birthCity")}{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <CitySelect
                            value={formData.birthCity}
                            apiEndpoint={`${environment.ELITE_BASE_URI}/geo/cities`}
                            nationId={formData.birthCountry?.id}
                            onValueChange={(city) =>
                              handleChange("birthCity", city)
                            }
                            placeholder={tGuest("anagraphicTab.selectCity")}
                          />
                          {errors.birthCity && (
                            <p className="text-xs text-destructive">
                              {errors.birthCity}
                            </p>
                          )}
                        </div>

                        {/* Bug 4: Codice fiscale opzionale (senza *) */}
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("anagraphicTab.taxCode")}
                          </Label>
                          <Input
                            placeholder={tGuest("placeholders.taxCode")}
                            value={formData.taxCode || ""}
                            onChange={(e) =>
                              handleChange(
                                "taxCode",
                                e.target.value.toUpperCase(),
                              )
                            }
                            className={
                              errors.taxCode
                                ? "border-destructive uppercase"
                                : "uppercase"
                            }
                          />
                          {errors.taxCode && (
                            <p className="text-xs text-destructive">
                              {errors.taxCode}
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      /* Bug 2: Città di nascita con suggerimenti CitySelect per stranieri (se vuoto usa la nazione) */
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">
                          {tGuest("anagraphicTab.birthCity")}
                        </Label>
                        <CitySelect
                          value={formData.birthCity}
                          apiEndpoint={`${environment.ELITE_BASE_URI}/geo/cities`}
                          nationId={formData.birthCountry?.id}
                          onValueChange={(city) =>
                            handleChange("birthCity", city)
                          }
                          placeholder={
                            formData.birthCountry?.name ||
                            tGuest("anagraphicTab.selectCity")
                          }
                          disabled={!formData.birthCountry}
                        />
                        {errors.birthCity && (
                          <p className="text-xs text-destructive">
                            {errors.birthCity}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Residenza */}
                <div className="pt-2 border-t">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                    <FileText className="size-4 text-primary" />
                    {tGuest("anagraphicTab.residenceTitle")}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.residenceCountry")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <CountrySelect
                        value={formData.residenceCountry}
                        apiEndpoint={`${environment.ELITE_BASE_URI}/geo/countries`}
                        onValueChange={(country) =>
                          handleChange("residenceCountry", country)
                        }
                        placeholder={tGuest("anagraphicTab.selectCountry")}
                      />
                      {errors.residenceCountry && (
                        <p className="text-xs text-destructive">
                          {errors.residenceCountry}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.residenceAddress")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        placeholder={tGuest("placeholders.residenceAddress")}
                        value={formData.residenceAddress || ""}
                        onChange={(e) =>
                          handleChange("residenceAddress", e.target.value)
                        }
                        className={
                          errors.residenceAddress ? "border-destructive" : ""
                        }
                      />
                      {errors.residenceAddress && (
                        <p className="text-xs text-destructive">
                          {errors.residenceAddress}
                        </p>
                      )}
                    </div>

                    {formData.residenceCountry?.iso2?.toUpperCase() === "IT" ? (
                      <>
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("anagraphicTab.residenceProvince")}{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <ProvinceSelect
                            value={formData.residenceProvince}
                            onValueChange={(prov) =>
                              handleChange("residenceProvince", prov)
                            }
                            placeholder={tGuest(
                              "anagraphicTab.selectProvince",
                            )}
                          />
                          {errors.residenceProvince && (
                            <p className="text-xs text-destructive">
                              {errors.residenceProvince}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("anagraphicTab.residenceCity")}{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <CitySelect
                            value={formData.residenceCity}
                            apiEndpoint={`${environment.ELITE_BASE_URI}/geo/cities`}
                            nationId={formData.residenceCountry?.id}
                            onValueChange={(city) =>
                              handleChange("residenceCity", city)
                            }
                            placeholder={tGuest("anagraphicTab.selectCity")}
                          />
                          {errors.residenceCity && (
                            <p className="text-xs text-destructive">
                              {errors.residenceCity}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("anagraphicTab.cap")}{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            placeholder="es. 00100"
                            value={formData.cap || ""}
                            onChange={(e) =>
                              handleChange("cap", e.target.value)
                            }
                            className={errors.cap ? "border-destructive" : ""}
                          />
                          {errors.cap && (
                            <p className="text-xs text-destructive">
                              {errors.cap}
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      /* Bug 2: Città di residenza con suggerimenti CitySelect per stranieri (se vuoto usa la nazione) */
                      <>
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("anagraphicTab.residenceCity")}
                          </Label>
                          <CitySelect
                            value={formData.residenceCity}
                            apiEndpoint={`${environment.ELITE_BASE_URI}/geo/cities`}
                            nationId={formData.residenceCountry?.id}
                            onValueChange={(city) =>
                              handleChange("residenceCity", city)
                            }
                            placeholder={
                              formData.residenceCountry?.name ||
                              tGuest("anagraphicTab.selectCity")
                            }
                            disabled={!formData.residenceCountry}
                          />
                          {errors.residenceCity && (
                            <p className="text-xs text-destructive">
                              {errors.residenceCity}
                            </p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("anagraphicTab.cap")}
                          </Label>
                          <Input
                            placeholder="es. 28001"
                            value={formData.cap || ""}
                            onChange={(e) =>
                              handleChange("cap", e.target.value)
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Contatti */}
                <div className="pt-2 border-t">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                    <FileText className="size-4 text-primary" />
                    {tGuest("anagraphicTab.contactsTitle")}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.email")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        type="email"
                        placeholder={tGuest("placeholders.email")}
                        value={formData.email || ""}
                        onChange={(e) =>
                          handleChange("email", e.target.value)
                        }
                        className={errors.email ? "border-destructive" : ""}
                      />
                      {errors.email ? (
                        <p className="text-xs text-destructive">
                          {errors.email}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {tGuest(
                            "errors.required_email",
                            "L'email è obbligatoria. É possibile utilizzare quella del prenotante.",
                          )}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.phone")}
                      </Label>
                      <Input
                        type="tel"
                        placeholder={tGuest("placeholders.phone")}
                        value={formData.phoneNumber || ""}
                        onChange={(e) =>
                          handleChange("phoneNumber", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Estremi Documento */}
                <div className="pt-2 border-t">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
                    <IdCard className="size-4 text-primary" />
                    {tGuest("anagraphicTab.documentDetails")}
                  </h4>

                  {formData.isDocumentExpired && !formData.expirationDate && (
                    <Alert className="mb-4 bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-200">
                      <AlertIcon>
                        <AlertCircle className="size-4 text-amber-600" />
                      </AlertIcon>
                      <div>
                        <AlertTitle className="text-xs sm:text-sm font-semibold">
                          {tGuest(
                            "documentTab.docExpiredWarningTitle",
                            "Attenzione: Documento scaduto",
                          )}
                        </AlertTitle>
                        <AlertDescription className="text-xs text-amber-800 dark:text-amber-300">
                          {tGuest(
                            "documentTab.docExpiredWarningDesc",
                            "La data di scadenza del documento scansionato è superata. Il campo è stato lasciato vuoto: inserire un documento valido.",
                          )}
                        </AlertDescription>
                      </div>
                    </Alert>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.docType")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={formData.documentType || ""}
                        onValueChange={(val) =>
                          handleChange("documentType", val)
                        }
                      >
                        <SelectTrigger
                          className={`w-full ${errors.documentType ? "border-destructive" : ""}`}
                        >
                          <SelectValue
                            placeholder={tGuest(
                              "anagraphicTab.selectDocType",
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {allowedDocumentTypes.includes("ID_CARD") && (
                            <SelectItem value="ID_CARD">
                              {tGuest("anagraphicTab.idCard")}
                            </SelectItem>
                          )}
                          {allowedDocumentTypes.includes("PASSPORT") && (
                            <SelectItem value="PASSPORT">
                              {tGuest("anagraphicTab.passport")}
                            </SelectItem>
                          )}
                          {allowedDocumentTypes.includes("DRIVER_LICENSE") && (
                            <SelectItem value="DRIVER_LICENSE">
                              {tGuest("anagraphicTab.driverLicense")}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {errors.documentType && (
                        <p className="text-xs text-destructive">
                          {errors.documentType}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.docNumber")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        placeholder="es. CA12345AA"
                        value={formData.documentNumber || ""}
                        onChange={(e) =>
                          handleChange(
                            "documentNumber",
                            e.target.value.toUpperCase(),
                          )
                        }
                        className={`uppercase ${errors.documentNumber ? "border-destructive" : ""}`}
                      />
                      {errors.documentNumber && (
                        <p className="text-xs text-destructive">
                          {errors.documentNumber}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.issuingCountry")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <CountrySelect
                        value={formData.issuingCountry}
                        apiEndpoint={`${environment.ELITE_BASE_URI}/geo/countries`}
                        onValueChange={(country) => {
                          handleChange("issuingCountry", country);
                          if (country?.iso2?.toUpperCase() !== "IT") {
                            handleChange("issuingCity", null);
                          }
                        }}
                        placeholder={tGuest(
                          "anagraphicTab.selectIssuingCountry",
                        )}
                        className={
                          errors.issuingCountry ? "border-destructive" : ""
                        }
                      />
                      {errors.issuingCountry && (
                        <p className="text-xs text-destructive">
                          {errors.issuingCountry}
                        </p>
                      )}
                    </div>

                    {formData.issuingCountry?.iso2?.toUpperCase() === "IT" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">
                          {tGuest("anagraphicTab.issuingCity")}{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <CitySelect
                          value={formData.issuingCity}
                          apiEndpoint={`${environment.ELITE_BASE_URI}/geo/cities`}
                          nationId={formData.issuingCountry?.id}
                          onValueChange={(city) =>
                            handleChange("issuingCity", city)
                          }
                          placeholder={tGuest(
                            "anagraphicTab.selectIssuingCity",
                          )}
                        />
                        {errors.issuingCity && (
                          <p className="text-xs text-destructive">
                            {errors.issuingCity}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.issuingAuthority")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={formData.issuingAuthority || ""}
                        onValueChange={(val) =>
                          handleChange("issuingAuthority", val)
                        }
                      >
                        <SelectTrigger
                          className={`w-full ${errors.issuingAuthority ? "border-destructive" : ""}`}
                        >
                          <SelectValue
                            placeholder={tGuest(
                              "anagraphicTab.selectIssuingAuthority",
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            "Carabinieri",
                            "Consolato",
                            "Comune",
                            "Ministero",
                            "Motorizzazione",
                            "Prefettura",
                            "Polizia",
                            "Questura",
                            "Altro",
                          ].map((authority) => (
                            <SelectItem key={authority} value={authority}>
                              {tGuest(
                                `anagraphicTab.issuingAuthorities.${authority}`,
                                authority,
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.issuingAuthority && (
                        <p className="text-xs text-destructive">
                          {errors.issuingAuthority}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.issuingDate")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <DatePicker
                        value={formData.issuingDate ?? undefined}
                        onChange={(date) =>
                          handleChange("issuingDate", date)
                        }
                        maxDate={new Date()}
                        placeholder="GG/MM/AAAA"
                        className={`w-full ${errors.issuingDate ? "border-destructive" : ""}`}
                      />
                      {errors.issuingDate && (
                        <p className="text-xs text-destructive">
                          {errors.issuingDate}
                        </p>
                      )}
                    </div>

                    {/* Bug 3: Validazione e minDate per data di scadenza documento */}
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        {tGuest("anagraphicTab.expirationDate")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <DatePicker
                        value={formData.expirationDate ?? undefined}
                        onChange={(date) =>
                          handleChange("expirationDate", date)
                        }
                        minDate={new Date()}
                        placeholder="GG/MM/AAAA"
                        className={`w-full ${errors.expirationDate ? "border-destructive" : ""}`}
                      />
                      {errors.expirationDate && (
                        <p className="text-xs text-destructive">
                          {errors.expirationDate}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t flex justify-end">
                  <Button
                    type="button"
                    onClick={() => {
                      const normalized = applyForeignCityFallbacks(formData);
                      setFormData(normalized);
                      setActiveTab("privacy");
                    }}
                    className="w-full sm:w-auto"
                  >
                    {tGuest("anagraphicTab.continuePrivacy")}
                  </Button>
                </div>
              </div>
            )}

            {/* TAB 3: PRIVACY & FATTURAZIONE */}
            {activeTab === "privacy" && (
              <div className="space-y-6 animate-in fade-in duration-200">
                {/* Privacy Policy */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                    <Lock className="size-4 text-primary" />
                    {tGuest("privacyTab.title")}
                  </h4>

                  {/* Bug 6: Default false + info icon (i) per visualizzare il testo */}
                  <div
                    className={`p-4 rounded-xl border flex items-start gap-3 transition-colors ${
                      errors.privacyPolicy
                        ? "border-destructive bg-destructive/5"
                        : "bg-muted/20"
                    }`}
                  >
                    <Checkbox
                      id="privacy-policy"
                      checked={formData.privacyPolicy || false}
                      onCheckedChange={(checked) =>
                        handleChange("privacyPolicy", !!checked)
                      }
                      className="mt-0.5"
                    />
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-1.5">
                        <Label
                          htmlFor="privacy-policy"
                          className="text-xs sm:text-sm font-medium cursor-pointer"
                        >
                          {tGuest("privacyTab.privacyPolicy")}{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPolicyModalType("privacy");
                            setIsPrivacyInfoDialogOpen(true);
                          }}
                          className="inline-flex items-center justify-center size-5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors cursor-pointer"
                          title={tGuest("privacyTab.privacyPolicyView")}
                          aria-label={tGuest("privacyTab.privacyPolicyView")}
                        >
                          <Info className="size-3.5 text-primary" />
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {tGuest("privacyTab.privacyPolicyDesc")}
                      </p>
                      {errors.privacyPolicy && (
                        <p className="text-xs font-semibold text-destructive mt-1">
                          {errors.privacyPolicy}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border bg-muted/10 flex items-start gap-3">
                    <Checkbox
                      id="marketing-policy"
                      checked={formData.marketingPolicy || false}
                      onCheckedChange={(checked) =>
                        handleChange("marketingPolicy", !!checked)
                      }
                      className="mt-0.5"
                    />
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-1.5">
                        <Label
                          htmlFor="marketing-policy"
                          className="text-xs sm:text-sm font-medium cursor-pointer"
                        >
                          {tGuest("privacyTab.marketingConsent")}
                        </Label>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPolicyModalType("marketing");
                            setIsPrivacyInfoDialogOpen(true);
                          }}
                          className="inline-flex items-center justify-center size-5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors cursor-pointer"
                          title={tGuest("privacyTab.marketingConsent")}
                          aria-label={tGuest("privacyTab.marketingConsent")}
                        >
                          <Info className="size-3.5 text-primary" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Richiesta Fattura */}
                <div className="pt-2 border-t space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <h4 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                        <Receipt className="size-4 text-primary" />
                        {tGuest("privacyTab.billingTitle")}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {tGuest("privacyTab.requestInvoice")}
                      </p>
                    </div>
                    <Checkbox
                      id="invoice-request"
                      checked={formData.invoiceRequest || false}
                      onCheckedChange={(checked) =>
                        handleChange("invoiceRequest", !!checked)
                      }
                    />
                  </div>

                  {formData.invoiceRequest && (
                    <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-4 animate-in fade-in-50 duration-200">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("privacyTab.companyName")}{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            placeholder="es. Acme S.r.l."
                            value={formData.companyName || ""}
                            onChange={(e) =>
                              handleChange("companyName", e.target.value)
                            }
                            className={
                              errors.companyName ? "border-destructive" : ""
                            }
                          />
                          {errors.companyName && (
                            <p className="text-xs text-destructive">
                              {errors.companyName}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("privacyTab.vatNumber")}{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            placeholder="es. 01234567890"
                            value={formData.vatNumber || ""}
                            onChange={(e) =>
                              handleChange("vatNumber", e.target.value)
                            }
                            className={
                              errors.vatNumber ? "border-destructive" : ""
                            }
                          />
                          {errors.vatNumber && (
                            <p className="text-xs text-destructive">
                              {errors.vatNumber}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1.5 sm:col-span-2">
                          <Label className="text-xs font-medium">
                            {tGuest("privacyTab.invoiceAddress")}{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            placeholder="es. Via Garibaldi, 10"
                            value={formData.invoiceAddress || ""}
                            onChange={(e) =>
                              handleChange("invoiceAddress", e.target.value)
                            }
                            className={
                              errors.invoiceAddress ? "border-destructive" : ""
                            }
                          />
                          {errors.invoiceAddress && (
                            <p className="text-xs text-destructive">
                              {errors.invoiceAddress}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("privacyTab.invoiceCity")}{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            placeholder="es. Milano"
                            value={formData.invoiceCity || ""}
                            onChange={(e) =>
                              handleChange("invoiceCity", e.target.value)
                            }
                            className={
                              errors.invoiceCity ? "border-destructive" : ""
                            }
                          />
                          {errors.invoiceCity && (
                            <p className="text-xs text-destructive">
                              {errors.invoiceCity}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("privacyTab.invoiceCap")}{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            placeholder="es. 20121"
                            value={formData.invoiceCap || ""}
                            onChange={(e) =>
                              handleChange("invoiceCap", e.target.value)
                            }
                            className={
                              errors.invoiceCap ? "border-destructive" : ""
                            }
                          />
                          {errors.invoiceCap && (
                            <p className="text-xs text-destructive">
                              {errors.invoiceCap}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("privacyTab.recipientCode")}
                          </Label>
                          <Input
                            placeholder="es. M5UXCR1 (o 0000000)"
                            value={formData.recipientCode || ""}
                            onChange={(e) =>
                              handleChange(
                                "recipientCode",
                                e.target.value.toUpperCase(),
                              )
                            }
                            className="uppercase"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">
                            {tGuest("privacyTab.pecEmail")}
                          </Label>
                          <Input
                            type="email"
                            placeholder="es. amministrazione@pec.it"
                            value={formData.pecEmail || ""}
                            onChange={(e) =>
                              handleChange("pecEmail", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </ScrollArea>

          {/* Footer with Actions */}
          <DialogFooter className="p-4 sm:p-5 border-t bg-muted/20 flex flex-col sm:flex-row items-center justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsConfirmCancelOpen(true)}
              className="w-full sm:w-auto"
            >
              {tGuest("buttons.cancel")}
            </Button>

            <Button
              type="button"
              variant="default"
              onClick={handleSave}
              className="w-full sm:w-auto px-6 font-semibold"
            >
              {tGuest("buttons.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog verifica anagrafica esistente su PMS / CRM */}
      <PMSUserVerificationDialog
        isOpen={isPmsVerificationOpen}
        onOpenChange={setIsPmsVerificationOpen}
        onUserSelected={(userId, fromPms) => {
          if (userId) {
            if (fromPms) {
              setFormData((prev) => ({
                ...prev,
                pmsId: userId,
                crmId: undefined,
              }));
            } else {
              setFormData((prev) => ({
                ...prev,
                crmId: userId,
                pmsId: undefined,
              }));
            }
          } else {
            setFormData((prev) => ({
              ...prev,
              pmsId: undefined,
              crmId: undefined,
            }));
          }
        }}
        searchData={{
          name:
            [formData.name, formData.surname]
              .filter(Boolean)
              .join(" ")
              .trim() || undefined,
          email: formData.email || undefined,
          dateOfBirth:
            formData.birthDate instanceof Date &&
            !isNaN(formData.birthDate.getTime())
              ? `${formData.birthDate.getFullYear()}-${String(formData.birthDate.getMonth() + 1).padStart(2, "0")}-${String(formData.birthDate.getDate()).padStart(2, "0")}`
              : undefined,
          fiscalCode: formData.taxCode || undefined,
        }}
      />

      {/* Modale con testo informativa Privacy / Marketing */}
      <PrivacyPolicyDialog
        open={isPrivacyInfoDialogOpen}
        onOpenChange={setIsPrivacyInfoDialogOpen}
        title={
          policyModalType === "marketing"
            ? tGuest("privacyTab.marketingConsent")
            : tGuest("privacyTab.privacyPolicy")
        }
        htmlContent={
          policyModalType === "marketing"
            ? marketingPolicyHtml
            : privacyPolicyHtml
        }
        fallbackText={tGuest("privacyTab.privacyPolicyDesc")}
        closeLabel={tGuest("buttons.close")}
      />

      {/* Chiedere conferma sul tasto annulla / chiusura */}
      <AlertDialog
        open={isConfirmCancelOpen}
        onOpenChange={setIsConfirmCancelOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {tGuest("cancelConfirmation.title", "Annullare l'inserimento?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {tGuest(
                "cancelConfirmation.description",
                "Le informazioni inserite non salvate andranno perse. Sei sicuro di voler annullare?",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {tGuest("cancelConfirmation.stay", "Continua a compilare")}
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: "destructive" }))}
              onClick={() => {
                setIsConfirmCancelOpen(false);
                onOpenChange(false);
              }}
            >
              {tGuest("cancelConfirmation.discard", "Sì, annulla")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
